import React, { useEffect, useRef } from 'react';
import { Box, Typography, keyframes, styled } from '@mui/material';

interface LoadingAnimationProps {
  message?: string;
}

const typewriter = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
`;

const thinkingDot = keyframes`
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 32,
  minHeight: 240,
});

const CanvasContainer = styled(Box)({
  width: 280,
  height: 160,
  marginBottom: 24,
});

const MessageBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
});

const MessageText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  letterSpacing: '0.5px',
}));

const Cursor = styled(Box)(({ theme }) => ({
  width: 2,
  height: 18,
  backgroundColor: theme.palette.primary.main,
  animation: `${typewriter} 1s step-end infinite`,
  marginLeft: 2,
}));

const DotsContainer = styled(Box)({
  display: 'flex',
  gap: 6,
  marginTop: 16,
});

const ThinkingDot = styled(Box)<{ delay: number }>(({ theme, delay }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  animation: `${thinkingDot} 1.4s ease-in-out infinite`,
  animationDelay: `${delay}ms`,
}));

/**
 * Siri-style multi-line wave visualization
 * Adapted from CanvasAudioVisualizer
 */
class WaveVisualizer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width: number = 0;
  private height: number = 0;
  private dpr: number = 1;
  private cy: number = 0;
  private animationFrame: number | null = null;
  private state = {
    t: 0,
    energy: 0,
    energySmoothed: 0,
  };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    this.ctx = ctx;
    this.resizeInternal();
  }

  private resizeInternal(): void {
    const cssW = this.canvas.clientWidth;
    const cssH = this.canvas.clientHeight;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.floor(cssW * this.dpr);
    this.canvas.height = Math.floor(cssH * this.dpr);
    this.width = this.canvas.width;
    this.height = this.canvas.height;
    this.cy = this.height / 2;
  }

  resize(): void {
    this.resizeInternal();
  }

  private demoEnergy(t: number): number {
    return (
      0.35 +
      Math.abs(Math.sin(t * 0.5) * 0.35 + Math.sin(t * 0.18) * 0.2 + Math.sin(t * 0.82) * 0.15)
    );
  }

  private drawMultiLineWave(
    baseY: number,
    amplitude: number,
    color: string,
    lineCount: number,
    spacing: number,
    frequency: number,
    phase: number,
    energyFactor: number
  ): void {
    const segments = 200;

    for (let line = 0; line < lineCount; line++) {
      const lineOffset = (line - lineCount / 2) * spacing;
      const lineAlpha = 1 - (Math.abs(line - lineCount / 2) / (lineCount / 2)) * 0.6;

      this.ctx.beginPath();

      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const x = t * this.width;

        // Sharp spindle envelope
        const envelope = Math.pow(Math.sin(t * Math.PI), 2.5);
        const scaledLineOffset = lineOffset * envelope;
        let y = baseY + scaledLineOffset;

        // Wave calculation
        const w1 = Math.sin(t * Math.PI * frequency + phase + line * 0.1);
        const w2 = Math.sin(t * Math.PI * frequency * 1.4 - phase * 0.6 + line * 0.08);
        const w3 = Math.cos(t * Math.PI * frequency * 0.9 + phase * 1.1 + line * 0.12);
        const waveValue = w1 * 0.45 + w2 * 0.35 + w3 * 0.2;

        y +=
          waveValue *
          amplitude *
          energyFactor *
          envelope *
          (1 - (Math.abs(lineOffset) / ((spacing * lineCount) / 2)) * 0.4);

        if (i === 0) {
          this.ctx.moveTo(x, y);
        } else {
          this.ctx.lineTo(x, y);
        }
      }

      const rgb = color.match(/\d+/g);
      if (rgb) {
        this.ctx.strokeStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${lineAlpha * 0.25})`;
      }
      this.ctx.lineWidth = Math.max(1.5, this.height * 0.004);
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.stroke();
    }
  }

  private animate = (ts: number): void => {
    this.state.t = ts * 0.001;

    // Clear canvas
    this.ctx.clearRect(0, 0, this.width, this.height);

    // Update energy using demo mode
    this.state.energy = this.demoEnergy(this.state.t);
    this.state.energySmoothed = this.state.energySmoothed * 0.92 + this.state.energy * 0.08;

    // Dynamic sizing based on energy
    const energyScale = 0.15 + this.state.energySmoothed * 0.7;
    const amp = this.height * 0.65 * energyScale;

    const energyMult = 0.6 + this.state.energySmoothed * 0.4;
    const lineCount = Math.floor(40 + this.state.energySmoothed * 30);
    const spacing = this.height * (0.004 + this.state.energySmoothed * 0.012);

    // Draw waves with different colors
    this.drawMultiLineWave(
      this.cy,
      amp * 0.85,
      'rgb(255, 100, 150)',
      lineCount,
      spacing,
      2.8,
      this.state.t * 1.1,
      energyMult
    );
    this.drawMultiLineWave(
      this.cy,
      amp * 0.95,
      'rgb(80, 140, 255)',
      lineCount,
      spacing,
      3.2,
      this.state.t * 1.4,
      energyMult
    );
    this.drawMultiLineWave(
      this.cy,
      amp,
      'rgb(50, 200, 255)',
      lineCount,
      spacing,
      2.6,
      this.state.t * 1.0,
      energyMult
    );
    this.drawMultiLineWave(
      this.cy,
      amp * 0.92,
      'rgb(180, 100, 240)',
      lineCount,
      spacing,
      3.0,
      this.state.t * 1.25,
      energyMult
    );
    this.drawMultiLineWave(
      this.cy,
      amp * 0.88,
      'rgb(80, 210, 190)',
      lineCount,
      spacing,
      2.7,
      this.state.t * 0.9,
      energyMult
    );

    this.animationFrame = requestAnimationFrame(this.animate);
  };

  start(): void {
    if (this.animationFrame) {
      return;
    }
    this.animate(performance.now());
  }

  stop(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.ctx.clearRect(0, 0, this.width, this.height);
  }

  destroy(): void {
    this.stop();
  }
}

const LoadingAnimation: React.FC<LoadingAnimationProps> = ({ message = 'Processing...' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const visualizerRef = useRef<WaveVisualizer | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Initialize visualizer
    visualizerRef.current = new WaveVisualizer(canvasRef.current);
    visualizerRef.current.start();

    // Handle resize
    const handleResize = () => {
      visualizerRef.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      visualizerRef.current?.destroy();
      visualizerRef.current = null;
    };
  }, []);

  return (
    <Container>
      <CanvasContainer>
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />
      </CanvasContainer>

      <MessageBox>
        <MessageText variant="body1">{message}</MessageText>
        <Cursor />
      </MessageBox>

      <DotsContainer>
        {[0, 160, 320].map((delay, index) => (
          <ThinkingDot key={index} delay={delay} />
        ))}
      </DotsContainer>
    </Container>
  );
};

export default LoadingAnimation;
